import { callFunction } from '../firebase/FunctionsService';
import { usesFirebaseAuth } from '../FirebaseAuthService';
import {
  normalizeBillingConfig,
  normalizeBillingCustomer,
  normalizeSubscription,
} from '../../core/billing/subscriptionDoc';
import { toMillis } from '../../core/billing/docValues';
import type {
  BillingInvoiceRow,
  BillingOverview,
  BillingPaymentRow,
  ManualPaymentSubmission,
} from '../../core/billing/billingViews';

export type {
  BillingInvoiceRow,
  BillingOverview,
  BillingPaymentRow,
  ManualPaymentSubmission,
};

/**
 * Mock and demo runs have no billing backend. They get a fully entitled
 * subscription so the offline experience is not crippled by a paywall.
 */
function offlineOverview(uid: string): BillingOverview {
  return {
    assigned: true,
    subscription: normalizeSubscription(uid, {
      planId: 'platinum',
      status: 'active',
      rail: 'stripe',
      updatedAt: Date.now(),
      billingSetupComplete: true,
    }),
    customer: normalizeBillingCustomer(uid, { paymentMethod: { status: 'verified' } }),
    config: normalizeBillingConfig(null),
    houseIds: [],
    seatCount: 0,
    invoices: [],
    payments: [],
    renewalAmountMinor: 0,
  };
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? value as Record<string, unknown> : {};
}

function asInt(value: unknown): number {
  const num = Number(value);
  return Number.isFinite(num) ? Math.trunc(num) : 0;
}

function normalizeInvoice(raw: unknown): BillingInvoiceRow {
  const data = asRecord(raw);
  return {
    id: String(data.id ?? ''),
    totalMinor: asInt(data.totalMinor),
    paidMinor: asInt(data.paidMinor),
    currency: String(data.currency ?? 'USD'),
    issuedAtMs: toMillis(data.issuedAt) ?? 0,
    dueAtMs: toMillis(data.dueAt) ?? 0,
    state: String(data.state ?? 'open'),
    planId: String(data.planId ?? ''),
  };
}

function normalizePaymentRow(raw: unknown): BillingPaymentRow {
  const data = asRecord(raw);
  return {
    id: String(data.id ?? ''),
    amountMinor: asInt(data.amountMinor ?? data.amountUsdMinor),
    currency: String(data.currency ?? 'USD'),
    method: String(data.method ?? ''),
    status: String(data.status ?? ''),
    submittedAtMs: toMillis(data.submittedAt) ?? 0,
    invoiceId: String(data.invoiceId ?? ''),
  };
}

function mapRows<T>(raw: unknown, map: (row: unknown) => T): T[] {
  return Array.isArray(raw) ? raw.map(map) : [];
}

export async function fetchBillingOverview(uid: string): Promise<BillingOverview> {
  if (!usesFirebaseAuth()) return offlineOverview(uid);

  const raw = asRecord(await callFunction('getBillingOverview'));
  const bankerUid = String(raw.bankerUid ?? uid);
  const subscription = normalizeSubscription(bankerUid, raw.subscription);
  return {
    assigned: raw.assigned === true || subscription.updatedAtMs != null,
    subscription,
    customer: normalizeBillingCustomer(bankerUid, raw.customer),
    config: normalizeBillingConfig(raw.config),
    houseIds: Array.isArray(raw.houseIds) ? raw.houseIds.map(String) : [],
    seatCount: Number(raw.seatCount ?? 0) || 0,
    invoices: mapRows(raw.invoices, normalizeInvoice),
    payments: mapRows(raw.payments, normalizePaymentRow),
    renewalAmountMinor: asInt(raw.renewalAmountMinor),
  };
}

export async function completeBillingOnboarding(input: {
  planId: string;
  rail: string;
}): Promise<{ needsPaymentMethod: boolean }> {
  const raw = asRecord(await callFunction('completeBillingOnboarding', input));
  return { needsPaymentMethod: raw.needsPaymentMethod === true };
}

export async function startSubscriptionTrial(planId: string): Promise<void> {
  await callFunction('startSubscriptionTrial', { planId });
}

export async function changeSubscriptionPlan(planId: string): Promise<void> {
  await callFunction('changeSubscriptionPlan', { planId });
}

export async function cancelSubscription(): Promise<void> {
  await callFunction('cancelSubscription');
}

export async function resumeSubscription(): Promise<void> {
  await callFunction('resumeSubscription');
}

/** Hosted Checkout in setup mode: the card never touches our client. */
export async function startPaymentMethodSetup(planId: string): Promise<string | null> {
  const raw = asRecord(await callFunction('startPaymentMethodSetup', { planId }));
  return raw.url ? String(raw.url) : null;
}

export interface VerificationOutcome {
  verified: boolean;
  failureCode: string | null;
}

/** Re-run the funds hold on the card already on file. */
export async function verifyPaymentMethod(planId: string): Promise<VerificationOutcome> {
  const raw = asRecord(await callFunction('verifyPaymentMethod', { planId }));
  return {
    verified: raw.verified === true,
    failureCode: raw.failureCode ? String(raw.failureCode) : null,
  };
}

export async function startSubscriptionCheckout(
  planId: string,
  promoCode?: string | null,
): Promise<string | null> {
  const raw = asRecord(await callFunction('startSubscriptionCheckout', {
    planId,
    ...(promoCode ? { promoCode } : {}),
  }));
  return raw.url ? String(raw.url) : null;
}

/** Crypto rail: prepay a period through a hosted charge. */
export async function startCryptoCharge(
  planId: string,
  promoCode?: string | null,
): Promise<string | null> {
  const raw = asRecord(await callFunction('startCryptoCharge', {
    planId,
    ...(promoCode ? { promoCode } : {}),
  }));
  return raw.url ? String(raw.url) : null;
}

export async function previewPromoCode(planId: string, code: string): Promise<{
  code: string;
  listMinor: number;
  discountMinor: number;
  totalMinor: number;
}> {
  const raw = asRecord(await callFunction('previewPromoCode', { planId, code }));
  return {
    code: String(raw.code ?? code),
    listMinor: asInt(raw.listMinor),
    discountMinor: asInt(raw.discountMinor),
    totalMinor: asInt(raw.totalMinor),
  };
}

export async function openBillingPortal(): Promise<string | null> {
  const raw = asRecord(await callFunction('openBillingPortal'));
  return raw.url ? String(raw.url) : null;
}

export async function submitManualPayment(payload: ManualPaymentSubmission): Promise<void> {
  await callFunction('submitManualPayment', { ...payload });
}
